import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';
import unitsData from '../../data/units_merged.json';

const IMAGE_BASE = '/assets/drawable/';
const IMAGES = ['abluft_regelung_mit_erhitzer.PNG'];

type TextBlock = { heading: string; body: string };
const M1_KEY = 'M1';
const U1_KEY = 'M1_U1';
const PAGE_ID = 'm1_u1_x2';

function extractSection(fullText: string, startMarker: string, endMarker?: string) {
  if (!fullText) return '';
  const start = fullText.indexOf(startMarker);
  if (start === -1) return '';
  const from = start + startMarker.length;
  if (!endMarker) return fullText.substring(from).trim();
  const end = fullText.indexOf(endMarker, from);
  if (end === -1) return fullText.substring(from).trim();
  return fullText.substring(from, end).trim();
}

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();
  
  // Lade Overrides
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const unit = ((unitsData as any)[M1_KEY] || {})[U1_KEY] || null;
  const title = '1.1.2 Abluftkaskade';
  const fullText: string = unit?.text || '';
  const section = extractSection(fullText, '-- X2_Abluftkaskade --', '-- X4_Adiabatik --') || 'Inhalt für Abluftkaskade nicht gefunden.';
  const structured = React.useMemo<TextBlock[]>(() => {
    // Paragraph-basierte Aufteilung statt Zeilen
    const paragraphs = section.split('\n\n').filter(p => p.trim());
    
    const blocks: TextBlock[] = [];
    paragraphs.forEach((para, idx) => {
      const lines = para.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length === 0) return;
      
      const blockId = `block_${idx}`;
      // Prüfe ob erste Zeile ein Heading ist (endet mit :)
      if (lines[0] && /[:：]$/.test(lines[0])) {
        blocks.push({
          heading: lines[0].replace(/[:：]$/, ''),
          body: override?.texts?.[blockId] || lines.slice(1).join('\n')
        });
      } else {
        blocks.push({
          heading: '',
          body: override?.texts?.[blockId] || lines.join('\n')
        });
      }
    });

    return blocks;
  }, [section, override]);

  // TTS: Concatenate all text for speech
  const fullPlainText = React.useMemo(() => {
    return structured.map(b => `${b.heading ? b.heading + '. ' : ''}${b.body}`).join(' ');
  }, [structured]);

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  // Admin: Bild ersetzen
  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canUploadImagesM1) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      refresh();
    } catch (e: any) {
      console.error('Bild-Upload fehlgeschlagen:', e);
      alert('Fehler beim Upload: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImagesM1]);

  // Admin: Text bearbeiten
  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canEditTextsM1) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      refresh();
    } catch (e: any) {
      console.error('Text-Speichern fehlgeschlagen:', e);
      alert('Fehler beim Speichern: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTextsM1]);

  // TTS Button Action
  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return (
    <UnitPageShell title={title} actions={ttsButton}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Bilder-Spalte */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {IMAGES.map((img, idx) => {
            const imageKey = `img_${idx}`;
            const v = override?.images?.[imageKey];
            const imageSrc = v ? (String(v).startsWith('/assets/') || String(v).startsWith('assets/') ? v : `/assets/unit_custom/${v}`) : `${IMAGE_BASE}${img}`;
            
            return (
              <ZoomableImage
                key={img}
                src={imageSrc}
                alt={img}
                label="Bild einfügen"
                onReplace={canUploadImagesM1 ? (file: File) => handleImageReplace(imageKey, file) : undefined}
              />
            );
          })}
        </Box>

        {/* Text-Spalte */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {structured.map((block, i) => (
            <EditableTextBlock
              key={i}
              pageId={PAGE_ID}
              blockId={`block_${i}`}
              heading={block.heading}
              body={block.body}
              onSave={(newBody: string) => handleTextSave(`block_${i}`, newBody)}
            />
          ))}

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/units/m1_u1_x99_quiz')}>
              Zum Quiz
            </Button>
          </Box>
        </Box>
      </Box>
    </UnitPageShell>
  );
};

export default Page;


